<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useSidebarPinnedConversations } from 'dashboard/composables/useSidebarPinnedConversations';

// Label + unread badge for a "pin to left menu" entry. Non-interactive on
// purpose: it renders inside SidebarGroupLeaf's router-link, so anything
// clickable here would nest interactive elements. Reads the conversation
// from the store internally so unread updates re-render this leaf only,
// not the whole sidebar menuItems tree.
const props = defineProps({
  pin: { type: Object, required: true },
});

const conversationById = useMapGetter('getConversationById');
const getContact = useMapGetter('contacts/getContact');
const { unavailablePinIds } = useSidebarPinnedConversations();

const conversation = computed(() => conversationById.value(props.pin.id));

// Deleted-or-inaccessible pins render muted on the stored title, no badge.
const isUnavailable = computed(() =>
  unavailablePinIds.value.has(Number(props.pin.id))
);

// Prefer the contacts store for the live name — contact.updated websocket
// events land there, not in conversation meta (same as ConversationCard).
const displayName = computed(() => {
  if (props.pin.label) return props.pin.label;
  const sender = conversation.value?.meta?.sender;
  const liveContact = sender?.id ? getContact.value(sender.id) : null;
  return (
    liveContact?.name || sender?.name || props.pin.title || `#${props.pin.id}`
  );
});

const unreadCount = computed(() => conversation.value?.unread_count || 0);
</script>

<template>
  <div
    class="flex flex-1 items-center gap-2 min-w-0"
    :class="{ 'opacity-50': isUnavailable }"
  >
    <span class="flex-1 truncate min-w-0 text-sm">{{ displayName }}</span>
    <!-- Same teal pill as ConversationCard.vue (canonical badge markup) —
    kept as a copy because there are only two sites (house rule: DRY at 3+) -->
    <span
      v-if="unreadCount"
      class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9 flex-shrink-0"
    >
      {{ unreadCount > 9 ? '9+' : unreadCount }}
    </span>
  </div>
</template>
